import type {
  CreditorWatchReadableReport,
  CreditorWatchReadableSummary,
  CreditorWatchReportAdverseField,
  CreditorWatchReportAdverseGroup,
  CreditorWatchReportAdverseItem,
  CreditorWatchReportAdverseTally,
  CreditorWatchRelatedEntity,
  CreditorWatchRelatedIndividual,
  CreditorWatchReportRiskTotals,
} from '../domain/credit.domain';
import { parseJSON } from './json';

// CreditorWatch returns 9999 rather than omitting the score when it has none.
const NO_CREDIT_SCORE = 9999;

// Only these keys hold money. Everything else is rendered as-is.
const AMOUNT_KEYS = new Set(['judgementAmount', 'amountOutstanding', 'debtAmount']);

// Keys whose camelCase does not humanise into something a credit officer would recognise.
const FIELD_LABELS: Record<string, string> = {
  abn: 'ABN',
  accountAdditionalAbn: 'Account Additional ABN',
  posterAbn: 'Poster ABN',
  doctype: 'Doc Type',
  lastmod: 'Last Modified',
  natureOfClaimDesc: 'Nature Of Claim Description',
  partpayment: 'Part Payment',
  partPaymentMade: 'Part Payment Made',
};

type AdverseKind =
  | 'paymentDefault'
  | 'courtJudgement'
  | 'insolvencyNotice'
  | 'mercantileEnquiry'
  | 'atoTaxDefault';

interface AdverseSection {
  key: string;
  kind: AdverseKind;
  // Plural section heading, and the singular used when the count is one.
  title: string;
  singular: string;
  // Short label for the at-a-glance tallies on an entity row.
  shortLabel: string;
  hiddenKeys?: Set<string>;
}

// Rendered in this order. `loans`, `creditEnquiries` and `anzsic` are deliberately absent — they
// are not adverse events. AZ-1634 asks for insolvency notices without their id or publisher blurb.
const ADVERSE_SECTIONS: AdverseSection[] = [
  { key: 'paymentDefaults', kind: 'paymentDefault', title: 'Payment Defaults', singular: 'Payment Default', shortLabel: 'default' },
  { key: 'courtJudgements', kind: 'courtJudgement', title: 'Court Judgements', singular: 'Court Judgement', shortLabel: 'judgement' },
  { key: 'atoTaxDefault', kind: 'atoTaxDefault', title: 'ATO Tax Default', singular: 'ATO Tax Default', shortLabel: 'ATO tax default' },
  { key: 'mercantileEnquiries', kind: 'mercantileEnquiry', title: 'Mercantile Enquiries', singular: 'Mercantile Enquiry', shortLabel: 'mercantile enquiry' },
  { key: 'insolvencyNotices', kind: 'insolvencyNotice', title: 'Insolvency Notices', singular: 'Insolvency Notice', shortLabel: 'insolvency notice', hiddenKeys: new Set(['id', 'publisher']) },
];

const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

const humaniseKey = (key: string): string => {
  if (FIELD_LABELS[key]) {
    return FIELD_LABELS[key];
  }
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const formatAmount = (value: unknown): string => {
  const amount = typeof value === 'number' ? value : parseFloat(String(value));
  if (!isFinite(amount)) {
    return String(value);
  }
  return `$${amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatFieldValue = (key: string, value: unknown): string => {
  if (AMOUNT_KEYS.has(key)) {
    return formatAmount(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const join = (parts: (string | null | undefined)[]): string =>
  parts.filter(part => !isBlank(part)).join(' \u00b7 ');

/**
 * One scannable line per adverse item, so the common questions — how much, who to, when — are
 * answered without opening the item's full field list.
 */
const headlineFor = (kind: AdverseKind, raw: Record<string, unknown>, fields: CreditorWatchReportAdverseField[]): string => {
  switch (kind) {
    case 'paymentDefault':
      return join([
        isBlank(raw['amountOutstanding']) ? null : formatAmount(raw['amountOutstanding']),
        raw['posterName'] as string,
        isBlank(raw['paymentDueDate']) ? null : `due ${raw['paymentDueDate']}`,
        raw['defaultSettled'] === true ? 'settled' : 'unsettled',
      ]);
    case 'courtJudgement':
      return join([
        isBlank(raw['judgementAmount']) ? null : formatAmount(raw['judgementAmount']),
        raw['plaintiff'] as string,
        raw['actionDate'] as string,
      ]);
    case 'atoTaxDefault':
      return join([
        isBlank(raw['debtAmount']) ? null : formatAmount(raw['debtAmount']),
        raw['createdAt'] as string,
      ]);
    case 'mercantileEnquiry':
      return join([
        (raw['company'] as string) ?? (raw['agent'] as string),
        raw['date'] as string,
      ]);
    case 'insolvencyNotice':
      return join([
        raw['title'] as string,
        (raw['date'] as string) ?? (raw['lastmod'] as string),
      ]);
    default:
      return fields.slice(0, 3).map(field => field.value).join(' \u00b7 ');
  }
};

// The adverse arrays are untyped in the API and their fields vary by item, so every non-blank key is
// rendered rather than a fixed field list.
const toAdverseItem = (raw: unknown, section: AdverseSection): CreditorWatchReportAdverseItem | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const hiddenKeys = section.hiddenKeys ?? new Set<string>();
  const fields: CreditorWatchReportAdverseField[] = [];
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (hiddenKeys.has(key) || isBlank(value)) {
      continue;
    }
    fields.push({ label: humaniseKey(key), value: formatFieldValue(key, value) });
  }
  if (!fields.length) {
    return null;
  }
  const headline = headlineFor(section.kind, raw as Record<string, unknown>, fields);
  return { headline: headline || fields[0].value, fields };
};

/**
 * Adverse groups for one `creditReport` block. The same block shape is returned for the subject
 * entity and for each related entity, so both go through here.
 */
const toAdverseGroups = (creditReport: unknown): CreditorWatchReportAdverseGroup[] => {
  if (!creditReport || typeof creditReport !== 'object') {
    return [];
  }
  const report = creditReport as Record<string, unknown>;
  const groups: CreditorWatchReportAdverseGroup[] = [];
  for (const section of ADVERSE_SECTIONS) {
    const raw = report[section.key];
    // `atoTaxDefault` is a single object where the rest are arrays.
    const entries = Array.isArray(raw) ? raw : isBlank(raw) ? [] : [raw];
    const items = entries
      .map(entry => toAdverseItem(entry, section))
      .filter((item): item is CreditorWatchReportAdverseItem => item !== null);
    if (items.length) {
      groups.push({ title: section.title, shortLabel: section.shortLabel, items });
    }
  }
  return groups;
};

const countAdverseItems = (groups: CreditorWatchReportAdverseGroup[]): number =>
  groups.reduce((total, group) => total + group.items.length, 0);

/**
 * Per-type counts such as "3 insolvency notices". The type matters more than the total: three
 * deregistration notices on a dormant shell read very differently to three payment defaults.
 */
const toTallies = (groups: CreditorWatchReportAdverseGroup[]): CreditorWatchReportAdverseTally[] =>
  groups.map(group => ({
    shortLabel: group.items.length === 1 ? group.shortLabel : `${group.shortLabel}s`,
    count: group.items.length,
  }));

const toSummary = (report: Record<string, any>): CreditorWatchReadableSummary => {
  const organisation = report['organisationExtract']?.organisation;
  const abrData = report['abrData'];
  const score = report['creditScore']?.currentCreditScore;
  return {
    organisationName: organisation?.organisationName ?? abrData?.name ?? null,
    australianBusinessNumber: organisation?.australianBusinessNumber ?? abrData?.abn ?? null,
    organisationNumber: organisation?.organisationNumber ?? abrData?.acn ?? null,
    currentCreditScore: typeof score === 'number' && score !== NO_CREDIT_SCORE ? score : null,
    currentCreditRating: report['creditRating']?.currentCreditRating ?? null,
  };
};

const personName = (parts: (string | null | undefined)[]): string =>
  parts.filter(part => !isBlank(part)).join(' ').trim();

const relatedEntityKey = (entity: Record<string, any>): string =>
  `${entity['organisationNumber'] ?? ''}|${entity['organisationName'] ?? ''}`;

// An organisation status that is itself an insolvency event. "Deregistered" is excluded: it is the
// single most common status on a director's related entities and usually just means dormant.
const ADVERSE_STATUS_TERMS = [
  'external administration',
  'liquidation',
  'winding up',
  'receiver',
  'administrator',
  'strike-off',
  'strike off',
];

const isAdverseStatus = (status: unknown): boolean => {
  if (isBlank(status)) {
    return false;
  }
  const lower = String(status).toLowerCase();
  return ADVERSE_STATUS_TERMS.some(term => lower.includes(term));
};

const toRelatedEntity = (raw: Record<string, any>): CreditorWatchRelatedEntity => {
  const adverseGroups = toAdverseGroups(raw['creditReport']);
  return {
    organisationName: raw['organisationName'] ?? null,
    organisationNumber: raw['organisationNumber'] ?? null,
    organisationStatus: raw['organisationStatus'] ?? null,
    statusIsAdverse: isAdverseStatus(raw['organisationStatus']),
    highRisk: raw['highRisk'] === true,
    adverseItemCount: countAdverseItems(adverseGroups),
    tallies: toTallies(adverseGroups),
    adverseGroups,
  };
};

// High risk first, then the entities carrying the most adverse items.
const sortRelatedEntities = (entities: CreditorWatchRelatedEntity[]): CreditorWatchRelatedEntity[] =>
  [...entities].sort((a, b) => {
    if (a.highRisk !== b.highRisk) {
      return a.highRisk ? -1 : 1;
    }
    return b.adverseItemCount - a.adverseItemCount;
  });

/**
 * Total shares on issue, used as the denominator for a member's holding. CreditorWatch reports a
 * share count per member and never a percentage, so the percentage has to be derived.
 */
const totalSharesOnIssue = (report: Record<string, any>): number => {
  const groups = report['organisationExtract']?.shareCapitalGroup;
  if (!Array.isArray(groups)) {
    return 0;
  }
  return groups.reduce((total: number, group: any) => {
    const shares = typeof group?.numberOfShares === 'number'
      ? group.numberOfShares
      : parseFloat(group?.numberOfShares);
    return total + (isFinite(shares) ? shares : 0);
  }, 0);
};

const shareholdingText = (numberHeld: unknown, beneficiallyOwned: unknown, totalShares: number): string => {
  const held = parseFloat(String(numberHeld));
  if (!isFinite(held)) {
    return '';
  }
  const ownership = beneficiallyOwned === false ? ' non-beneficial' : '';
  const heldText = held.toLocaleString('en-AU');
  if (totalShares <= 0) {
    return `${heldText} shares${ownership}`;
  }
  return `${((held / totalShares) * 100).toFixed(1)}% (${heldText} shares)${ownership}`;
};

interface IndividualAccumulator {
  fullName: string;
  dateOfBirth: string | null;
  roles: string[];
  relatedEntitiesByKey: Map<string, CreditorWatchRelatedEntity>;
  shareholdings: string[];
}

const newAccumulator = (fullName: string, dateOfBirth: string | null): IndividualAccumulator => ({
  fullName,
  dateOfBirth,
  roles: [],
  relatedEntitiesByKey: new Map(),
  shareholdings: [],
});

/**
 * Officeholders and shareholders, combined into one person per unique name and date of birth.
 *
 * CreditorWatch repeats a person once per role, so the same individual arrives several times.
 * Shareholders carry no date of birth, so a shareholder is merged into an officeholder only when
 * the name matches exactly one of them; otherwise they are listed separately without a date.
 */
const toRelatedIndividuals = (report: Record<string, any>): CreditorWatchRelatedIndividual[] => {
  const accumulators = new Map<string, IndividualAccumulator>();

  const officeHolders = report['organisationExtract']?.companyOfficeHolders;
  for (const holder of Array.isArray(officeHolders) ? officeHolders : []) {
    const fullName = personName([
      holder?.givenName1,
      holder?.givenName2,
      holder?.givenName3,
      holder?.familyName,
    ]) || (isBlank(holder?.officerName) ? '' : String(holder.officerName));
    if (!fullName) {
      continue;
    }
    const dateOfBirth = isBlank(holder?.officerBirthDate) ? null : String(holder.officerBirthDate);
    const key = `${fullName.toUpperCase()}|${dateOfBirth ?? ''}`;
    const accumulator = accumulators.get(key) ?? newAccumulator(fullName, dateOfBirth);
    accumulators.set(key, accumulator);

    if (!isBlank(holder?.roleType) && !accumulator.roles.includes(holder.roleType)) {
      accumulator.roles.push(holder.roleType);
    }
    for (const known of Array.isArray(holder?.knownInformation) ? holder.knownInformation : []) {
      if (!known || typeof known !== 'object') {
        continue;
      }
      const entityKey = relatedEntityKey(known);
      if (!accumulator.relatedEntitiesByKey.has(entityKey)) {
        accumulator.relatedEntitiesByKey.set(entityKey, toRelatedEntity(known));
      }
    }
  }

  const totalShares = totalSharesOnIssue(report);
  const shareInterests = report['organisationExtract']?.shareInterests;
  for (const interest of Array.isArray(shareInterests) ? shareInterests : []) {
    const holding = shareholdingText(interest?.numberHeld, interest?.beneficiallyOwned, totalShares);
    for (const member of Array.isArray(interest?.members) ? interest.members : []) {
      const fullName = personName([member?.givenName1, member?.givenName2, member?.familyName])
        || (isBlank(member?.memberName) ? '' : String(member.memberName));
      if (!fullName) {
        continue;
      }
      const nameKey = fullName.toUpperCase();
      const nameMatches = [...accumulators.values()].filter(
        candidate => candidate.fullName.toUpperCase() === nameKey,
      );
      const accumulator = nameMatches.length === 1
        ? nameMatches[0]
        : accumulators.get(`${nameKey}|`) ?? newAccumulator(fullName, null);
      accumulators.set(`${accumulator.fullName.toUpperCase()}|${accumulator.dateOfBirth ?? ''}`, accumulator);
      if (holding) {
        accumulator.shareholdings.push(holding);
      }
    }
  }

  return [...accumulators.values()].map(accumulator => {
    const entities = sortRelatedEntities([...accumulator.relatedEntitiesByKey.values()]);
    // Entities with nothing adverse, no risk flag and no insolvency status are the bulk of a long
    // report and are collapsed in the view, so they are separated here rather than in the template.
    const isFlagged = (entity: CreditorWatchRelatedEntity) =>
      entity.adverseItemCount > 0 || entity.highRisk || entity.statusIsAdverse;
    const flaggedEntities = entities.filter(isFlagged);
    const clearEntities = entities.filter(entity => !isFlagged(entity));
    return {
      fullName: accumulator.fullName,
      dateOfBirth: accumulator.dateOfBirth,
      roles: accumulator.roles,
      shareholding: accumulator.shareholdings.length ? accumulator.shareholdings.join(', ') : null,
      flaggedEntities,
      clearEntities,
      adverseItemCount: flaggedEntities.reduce((total, entity) => total + entity.adverseItemCount, 0),
      highRiskEntityCount: flaggedEntities.filter(entity => entity.highRisk).length,
    };
  });
};

const toRiskTotals = (
  entityAdverseGroups: CreditorWatchReportAdverseGroup[],
  individuals: CreditorWatchRelatedIndividual[],
): CreditorWatchReportRiskTotals => {
  const entityAdverseItemCount = countAdverseItems(entityAdverseGroups);
  const relatedAdverseItemCount = individuals.reduce((total, person) => total + person.adverseItemCount, 0);
  return {
    entityAdverseItemCount,
    relatedAdverseItemCount,
    highRiskEntityCount: individuals.reduce((total, person) => total + person.highRiskEntityCount, 0),
    relatedEntityCount: individuals.reduce(
      (total, person) => total + person.flaggedEntities.length + person.clearEntities.length,
      0,
    ),
    clean: entityAdverseItemCount === 0 && relatedAdverseItemCount === 0,
  };
};

const emptyReport = (): CreditorWatchReadableReport => ({
  summary: {
    organisationName: null,
    australianBusinessNumber: null,
    organisationNumber: null,
    currentCreditScore: null,
    currentCreditRating: null,
  },
  riskTotals: {
    entityAdverseItemCount: 0,
    relatedAdverseItemCount: 0,
    highRiskEntityCount: 0,
    relatedEntityCount: 0,
    clean: true,
  },
  entityAdverseGroups: [],
  relatedIndividuals: [],
});

/**
 * Reshapes a raw CreditorWatch report into the readable view used by the report dialog, keeping only
 * the data that bears on a credit decision. Accepts the stored value as-is, parsed or still a string.
 */
export const toCreditorWatchReadableReport = (json: unknown): CreditorWatchReadableReport => {
  const report = parseJSON(json);
  if (!report || typeof report !== 'object') {
    return emptyReport();
  }
  const entityAdverseGroups = toAdverseGroups(report['creditReport']);
  const relatedIndividuals = toRelatedIndividuals(report);
  return {
    summary: toSummary(report),
    riskTotals: toRiskTotals(entityAdverseGroups, relatedIndividuals),
    entityAdverseGroups,
    relatedIndividuals,
  };
};
